import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { useMicrocopy } from "../lib/microcopy";
import { Button } from "./button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./overlays";

export interface ConfirmOptions {
  title: ReactNode;
  /** What will happen, in the officer's terms (who is notified, what is published, what is locked). */
  description?: ReactNode;
  confirmLabel: ReactNode;
  cancelLabel?: ReactNode;
  /** "destructive" for withdrawals and deletions; the default for issuing, sending and publishing. */
  tone?: "default" | "destructive";
  /** Adds the "This cannot be undone" line. On by default: the dialog exists for irreversible actions. */
  irreversible?: boolean;
}

type Confirm = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<Confirm | null>(null);

/** One confirmation dialog for the whole app; `useConfirm()` opens it and resolves with the officer's answer. */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const m = useMicrocopy();
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  const confirm = useCallback<Confirm>((next) => {
    resolver.current?.(false);
    setOptions(next);
    return new Promise<boolean>((resolve) => { resolver.current = resolve; });
  }, []);

  const settle = (ok: boolean) => {
    resolver.current?.(ok);
    resolver.current = null;
    setOptions(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={options !== null} onOpenChange={(open) => { if (!open) settle(false); }}>
        <DialogContent size="sm" data-confirm-dialog>
          {options ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {options.tone === "destructive" ? <AlertTriangle className="size-5 shrink-0 text-danger-600" /> : null}
                  {options.title}
                </DialogTitle>
                {options.description || options.irreversible !== false ? (
                  <DialogDescription asChild>
                    <div className="space-y-2">
                      {options.description ? <div>{options.description}</div> : null}
                      {options.irreversible !== false ? <p className="font-medium text-strong">{m("irreversible")}</p> : null}
                    </div>
                  </DialogDescription>
                ) : null}
              </DialogHeader>
              <DialogFooter>
                <Button variant="ghost" onClick={() => settle(false)}>{options.cancelLabel ?? m("cancel")}</Button>
                <Button variant={options.tone === "destructive" ? "destructive" : "default"} data-primary-action onClick={() => settle(true)} autoFocus>
                  {options.confirmLabel}
                </Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

/**
 * Asks before an irreversible action. Outside a `ConfirmProvider` it falls back to the browser's dialog so a missing provider never
 * lets the action through unasked.
 */
export function useConfirm(): Confirm {
  const ctx = useContext(ConfirmContext);
  return ctx ?? (async (o) => window.confirm(typeof o.title === "string" ? o.title : ""));
}
